import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { calculateCurrentElapsedTime, restoreTimerState, saveTimerState, TimerState } from '../helpers/timerCloudStorage';

export interface UseTimerReturn {
  elapsedTime: number;
  isRunning: boolean;
  start: () => void;
  pause: () => void;
  setTime: (ms: number) => void;
}

export const useTimer = (initialTime: number, questId?: string): UseTimerReturn => {
  const [elapsedTime, setElapsedTime] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState<dayjs.Dayjs | null>(null);
  const [pausedTime, setPausedTime] = useState(initialTime);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Restore timer state from cloud storage
  const restoreFromCloudStorage = useCallback(async () => {
    if (!questId) {
      setInitialized(true);
      return;
    }

    try {
      const savedState = await restoreTimerState();
      if (savedState && savedState.questId === questId) {
        // Restore state for the same quest
        setPausedTime(savedState.pausedTime);
        
        // If timer was running, calculate current elapsed time
        if (savedState.isRunning && savedState.startTime) {
          const currentElapsed = calculateCurrentElapsedTime(savedState);
          setElapsedTime(currentElapsed);
          setStartTime(dayjs(savedState.startTime));
          setIsRunning(true);
        } else {
          setElapsedTime(savedState.pausedTime);
          setIsRunning(false);
        }
      }
    } catch (error) {
      console.error('Failed to restore timer state:', error);
    }
    setInitialized(true);
  }, [questId]);

  // Save state whenever it changes
  useEffect(() => {
    if (!initialized || !questId) return;

    const state: TimerState = {
      questId,
      startTime: startTime?.toISOString() || null,
      isRunning,
      pausedTime
    };

    saveTimerState(state);
  }, [questId, startTime, isRunning, pausedTime, initialized]);

  // Initialize timer from cloud storage on mount
  useEffect(() => {
    restoreFromCloudStorage();
  }, [restoreFromCloudStorage]);

  const setTime = useCallback((ms: number) => {
    setElapsedTime(ms);
    setPausedTime(ms);
    setStartTime(null);
    setIsRunning(false);
  }, []);

  const start = useCallback(() => {
    const now = dayjs();
    setStartTime(now);
    setIsRunning(true);
  }, []);

  const pause = useCallback(() => {
    setIsRunning(false);
    setPausedTime(elapsedTime);
    setStartTime(null);
  }, [elapsedTime]);

  useEffect(() => {
    if (isRunning && startTime) {
      intervalRef.current = setInterval(() => {
        const now = dayjs();
        const currentElapsed = pausedTime + now.diff(startTime);
        setElapsedTime(currentElapsed);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning, startTime, pausedTime]);

  return {
    elapsedTime,
    isRunning,
    start,
    pause,
    setTime,
  };
}; 